import type { KeyConfig } from "../components/Keyboard/types";
import { getKeyCodeInfo } from "./keyCodeMapper";

/**
 * 星闪 X87 HE / ES87：Mac Base 层 F4–F6 出厂组合键。
 * xsKeycode = 62288(0xF350) + HFD FUNC value16（与星闪固件 FUNC 页一致）。
 */
export const XINGSHAN_FUNC_COMBO_BASE = 62288;

export const XINGSHAN_FUNC_ALT_SPACE = XINGSHAN_FUNC_COMBO_BASE + 92; // 62380
export const XINGSHAN_FUNC_CTRL_DOWN = XINGSHAN_FUNC_COMBO_BASE + 50; // 62338
export const XINGSHAN_FUNC_WIN_SPACE = XINGSHAN_FUNC_COMBO_BASE + 88; // 62376

const WIN_F4 = 61;
const WIN_F5 = 62;
const WIN_F6 = 63;

/** 87 配列 F 行 Esc 后有一列占位(0)，F4–F6 在 col 5/6/7；es87 无占位，在 col 4/5/6 */
function getMacBaseFRowColumnMap(
  keyboardType: string
): Readonly<
  Record<number, { keyCode: number; label: string; winFKey: number }>
> {
  const f4Col = keyboardType === "87" ? 5 : 4;
  const f5Col = keyboardType === "87" ? 6 : 5;
  const f6Col = keyboardType === "87" ? 7 : 6;
  return {
    [f4Col]: {
      keyCode: XINGSHAN_FUNC_ALT_SPACE,
      label: "Alt + Space",
      winFKey: WIN_F4,
    },
    [f5Col]: {
      keyCode: XINGSHAN_FUNC_CTRL_DOWN,
      label: "Ctrl + ↓",
      winFKey: WIN_F5,
    },
    [f6Col]: {
      keyCode: XINGSHAN_FUNC_WIN_SPACE,
      label: "Win + Space",
      winFKey: WIN_F6,
    },
  };
}

export function shouldApplyX87MacBaseLayer(
  keyboardType: string,
  layer: number,
  system: number
): boolean {
  return (
    (keyboardType === "87" || keyboardType === "es87") &&
    layer === 0 &&
    system === 1
  );
}

function resolveMacBaseFRowKeyCode(
  sdkCell: number | undefined,
  staticCell: number,
  winFKey: number
): number {
  if (sdkCell === staticCell) return staticCell;
  if (sdkCell === undefined || sdkCell === 0 || sdkCell === winFKey) {
    return staticCell;
  }
  if (!getKeyCodeInfo(sdkCell)) return staticCell;
  return sdkCell;
}

/** SDK 矩阵：空键 / 标准 F4–F6 / 未知键码 → 出厂组合键 */
export function applyX87MacBaseLayerToSdkRows(
  rows: { layer: number; row: number; keyboardLayout: number[] }[],
  keyboardType: string,
  layer: number,
  system: number
): { layer: number; row: number; keyboardLayout: number[] }[] {
  if (!shouldApplyX87MacBaseLayer(keyboardType, layer, system)) {
    return rows;
  }
  const colMap = getMacBaseFRowColumnMap(keyboardType);
  return rows.map((row, rowIndex) => {
    const matrixRow = row.row ?? rowIndex;
    if (matrixRow !== 0) return row;
    const sdkLayout = row.keyboardLayout ?? [];
    const merged = [...sdkLayout];
    for (const [colStr, spec] of Object.entries(colMap)) {
      const col = Number(colStr);
      merged[col] = resolveMacBaseFRowKeyCode(
        merged[col],
        spec.keyCode,
        spec.winFKey
      );
    }
    return { ...row, keyboardLayout: merged };
  });
}

function shouldReplaceMacBaseFRowKey(
  key: KeyConfig,
  spec: { keyCode: number; label: string; winFKey: number }
): boolean {
  if (key.keyCode === spec.keyCode && key.label) return false;
  if (key.code === "__gap__" && key.keyCode === 0) return true;
  if (!key.label || key.code === "__NOT_EXIST_") return true;
  if (key.keyCode === 0 || key.keyCode === spec.winFKey) {
    return true;
  }
  return !getKeyCodeInfo(key.keyCode);
}

/** UI 层：将 F4–F6 空白占位键帽替换为组合键文字 */
export function applyX87MacBaseLayerToKeyConfigs(
  layout: KeyConfig[][],
  keyboardType: string,
  layer: number,
  system: number
): KeyConfig[][] {
  if (!shouldApplyX87MacBaseLayer(keyboardType, layer, system)) {
    return layout;
  }
  const colMap = getMacBaseFRowColumnMap(keyboardType);
  return layout.map((row, rowIndex) => {
    if (rowIndex !== 0) return row;
    return row.map((key) => {
      const col = key.originalCol;
      if (col == null || col < 0) return key;
      const spec = colMap[col];
      if (!spec || !shouldReplaceMacBaseFRowKey(key, spec)) return key;
      return {
        ...key,
        keyCode: spec.keyCode,
        label: spec.label,
        comm: spec.label,
        code: `KEY_${spec.keyCode}`,
      };
    });
  });
}
